export const fitnessTrainersData = {
  industry: "Fitness Trainers",
  slug: "fitness-trainers",
  
  seo: {
    title: "Intake Forms for Fitness Trainers | HeyIntake",
    description: "Onboard personal training clients faster with AI-powered intake forms. Capture fitness goals, health history, injuries, and availability before the first session.",
    keywords: [
      "personal trainer intake form",
      "fitness client questionnaire",
      "gym membership intake",
      "health history form",
      "fitness assessment form",
      "PT client onboarding"
    ]
  },

  heroTitle: "Intake Forms for Fitness Trainers",
  heroSubtitle: "Know your clients' goals, injuries, and availability before the first session. Onboard faster, train smarter.",
  heroDescription: "Stop spending the first session asking basic questions. HeyIntake captures fitness goals, health history, dietary preferences, and scheduling availability so you can start training from day one.",
  
  whyAIScoring: {
    title: "Why Fitness Trainers Need Professional Intake",
    intro: "The first session sets the tone for your entire client relationship. Wasting it on paperwork and basic questions isn't just inefficient — it's unprofessional.",
    paragraphs: [
      "Most trainers rely on clipboard forms or verbal interviews to gather client information. This leads to incomplete health histories, missed injury disclosures, and unclear goals. Worse, you look disorganized compared to trainers with streamlined onboarding.",
      "HeyIntake transforms your intake process. Before clients walk through the door, you have their complete health history, fitness goals, past injuries, dietary restrictions, and scheduling preferences. Our AI even flags potential red flags like recent surgeries or contraindicated conditions.",
      "For personal trainers, gym owners, and online coaches, this means safer programming, faster client results, and a professional image that justifies premium pricing."
    ]
  },

  painPoints: [
    {
      title: "Incomplete Health History",
      description: "Clients forget to mention injuries, surgeries, or medical conditions until they're already in pain during a session."
    },
    {
      title: "Unclear Goals",
      description: "Vague goals like 'get in shape' or 'lose weight' make it impossible to design effective programs."
    },
    {
      title: "Scheduling Conflicts",
      description: "Booking sessions only to discover the client can't actually make that time consistently."
    },
    {
      title: "Liability Concerns",
      description: "No documented health history or consent creates legal exposure for you and your business."
    },
    {
      title: "Wasted First Sessions",
      description: "Using precious training time to fill out forms instead of actually working out."
    },
    {
      title: "No-Show Clients",
      description: "Leads who inquire but never commit, wasting your time with back-and-forth messages."
    }
  ],

  exampleFields: [
    {
      category: "Contact & Basics",
      fields: [
        "Full name",
        "Email and phone",
        "Emergency contact",
        "How did you hear about me?",
        "Training location preference"
      ]
    },
    {
      category: "Health & Medical",
      fields: [
        "Current medications",
        "Past injuries or surgeries",
        "Chronic conditions (diabetes, heart disease, etc.)",
        "Current physical limitations",
        "Doctor clearance for exercise"
      ]
    },
    {
      category: "Fitness Background",
      fields: [
        "Current fitness level (beginner/intermediate/advanced)",
        "Exercise history",
        "Past experience with personal training",
        "Current workout routine (if any)",
        "Sports or activities you enjoy"
      ]
    },
    {
      category: "Goals & Preferences",
      fields: [
        "Primary fitness goal",
        "Target timeline",
        "Dietary preferences/restrictions",
        "Preferred training days and times",
        "Budget for training"
      ]
    }
  ],

  exampleIntakeForm: {
    title: "Example Personal Training Intake Form",
    description: "Here's what a professional fitness intake looks like:",
    fields: [
      { label: "What's your primary fitness goal?", type: "select", options: ["Weight loss", "Build muscle", "Improve endurance", "General fitness", "Sport-specific training"] },
      { label: "Current fitness level", type: "select", options: ["Beginner", "Intermediate", "Advanced"] },
      { label: "Any injuries or physical limitations?", type: "textarea" },
      { label: "Current medications (if any)", type: "textarea" },
      { label: "How many days per week can you train?", type: "select", options: ["1-2", "3-4", "5+"] },
      { label: "Preferred training times", type: "select", options: ["Early morning", "Mid-day", "Evening", "Flexible"] },
      { label: "Monthly training budget", type: "select", options: ["$200-$400", "$400-$600", "$600-$800", "$800+"] }
    ],
    cta: "Use this exact form → Start for $1"
  },

  howItWorks: {
    title: "How HeyIntake Works for Trainers",
    subtitle: "From inquiry to first session in three steps",
    steps: [
      {
        title: "Client Completes Intake",
        description: "New clients fill out your branded form with health history, goals, and availability."
      },
      {
        title: "AI Flags & Prioritizes",
        description: "Our AI identifies red flags, scores commitment level, and prioritizes serious inquiries."
      },
      {
        title: "Start Training Day One",
        description: "Walk into the first session prepared with a program tailored to their goals and limitations."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Client Screening for Trainers",
    description: "HeyIntake's AI helps you identify committed clients and flag potential health concerns before the first session.",
    factors: [
      "Commitment indicators (budget, schedule flexibility)",
      "Health red flags requiring doctor clearance",
      "Goal clarity and realistic expectations",
      "Past training experience and consistency",
      "Schedule compatibility with your availability"
    ],
    exampleLead: {
      name: "David Chen",
      context: "Weight Loss Goal • No Injuries • 4x/week Available",
      summary: "Looking to lose 30 lbs before summer wedding. No injuries, doctor cleared. Can train mornings before work. Budget aligns with premium package. High motivation.",
      score: 91
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Screen Health Risks",
      description: "AI flags clients who need doctor clearance before starting, protecting you from liability."
    },
    {
      icon: "file",
      title: "Collect PAR-Q Forms",
      description: "Physical Activity Readiness Questionnaire built into your intake process."
    },
    {
      icon: "clock",
      title: "Match Availability",
      description: "Know their schedule before booking so you don't waste time on conflicts."
    },
    {
      icon: "users",
      title: "Segment by Goal",
      description: "Route weight loss clients to different programs than muscle-building clients."
    },
    {
      icon: "zap",
      title: "Automate Onboarding",
      description: "Send welcome packets, waivers, and scheduling links automatically."
    },
    {
      icon: "chart",
      title: "Track Client Pipeline",
      description: "See who's inquired, who's onboarding, and who's actively training."
    }
  ],

  relatedIndustries: [
    { name: "Intake Forms for Med Spas", path: "/intake-form-for-med-spas" },
    { name: "Intake Forms for Coaches", path: "/intake-form-for-coaches" },
    { name: "Intake Forms for Therapists", path: "/intake-form-for-therapists" },
    { name: "Intake Forms for Photographers", path: "/intake-form-for-photographers" }
  ],

  ctaText: "Ready to Onboard Clients Faster?"
};
